import { JoonUIColor, SPACING } from "@joonapp/web-shared"
import { useState, useRef, useEffect, RefObject } from "react"

import { useSendbird } from "./SBProvider"

const MessagesListScrollButton = ({
  containerRef,
}: {
  containerRef: RefObject<HTMLDivElement>
}) => {
  const [showScrollButton, setShowScrollButton] = useState(false)
  const { messages, currentChannel } = useSendbird()
  useInitialScroll({
    containerRef,
    listItems: messages,
    dependencies: [currentChannel],
  })

  const checkScrollPosition = () => {
    const element = containerRef.current
    if (!element) return

    const isScrolledUp =
      element.scrollHeight - element.scrollTop - element.clientHeight > 100
    setShowScrollButton(isScrolledUp)
  }

  useEffect(() => {
    const element = containerRef.current
    if (!element) return

    element.addEventListener("scroll", checkScrollPosition)
    checkScrollPosition()

    return () => {
      element.removeEventListener("scroll", checkScrollPosition)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef])

  const scrollToBottom = (behavior: ScrollBehavior = "smooth") => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior,
      })
    }
  }

  if (!showScrollButton) return null

  return (
    <button
      onClick={() => scrollToBottom()}
      style={{
        position: "sticky",
        bottom: SPACING.space4,
        minHeight: SPACING.space10,
        maxHeight: SPACING.space10,
        minWidth: SPACING.space10,
        maxWidth: SPACING.space10,
        borderRadius: "50%",
        margin: "0 auto",
        backgroundColor: JoonUIColor.background.xlightGray,
        border: `1px solid ${JoonUIColor.border.default}`,
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1,
      }}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke={JoonUIColor.text.primary}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 19V5M5 12l7 7 7-7" />
      </svg>
    </button>
  )
}

export default MessagesListScrollButton

interface UseInitialScrollProps {
  containerRef: RefObject<HTMLDivElement>
  listItems: any[]
  dependencies?: any[]
}

// Will scroll to bottom of container when messages first load
// Will scroll to bottom of container when dependencies change
export const useInitialScroll = ({
  containerRef,
  listItems,
  dependencies = [],
}: UseInitialScrollProps) => {
  const isFirstLoad = useRef(true)

  const scrollToBottom = () => {
    if (!containerRef.current) return
    const messagesContainer = containerRef.current
    messagesContainer.scrollTo({
      top: messagesContainer.scrollHeight,
      behavior: "auto",
    })

    const images = messagesContainer.getElementsByTagName("img")
    if (images.length > 0) {
      Promise.all(
        Array.from(images).map((img) => {
          if (img.complete) return Promise.resolve()
          return new Promise((resolve) => {
            img.onload = resolve
            img.onerror = resolve
          })
        })
      ).then(() => {
        messagesContainer.scrollTo({
          top: messagesContainer.scrollHeight,
          behavior: "auto",
        })
      })
    }
  }

  useEffect(() => {
    if (!listItems.length || !isFirstLoad.current) return
    scrollToBottom()
    isFirstLoad.current = false
  }, [listItems])

  useEffect(() => scrollToBottom(), dependencies)
}
